import type { ParsedStatement, ParsedTransaction, PaymentMethod } from "@paisa/types"
import * as XLSX from "xlsx"
import { parseAmount } from "../lib/parse-helpers"

/** Column names in SBI savings account statements */
const COLUMNS = {
  date: "Date",
  details: "Details",
  refNo: "Ref No/Cheque No",
  debit: "Debit",
  credit: "Credit",
  balance: "Balance",
} as const

/**
 * Detect whether an xlsx file is an SBI savings account statement.
 */
export function isSbiSavings(rawContent: string, headers?: string[]): boolean {
  const hasSbiHeader = /State Bank of India/i.test(rawContent)
  const hasColumns =
    !!headers &&
    headers.includes(COLUMNS.details) &&
    headers.includes(COLUMNS.debit) &&
    headers.includes(COLUMNS.credit)
  return hasSbiHeader || hasColumns
}

/**
 * Parse an SBI savings account XLSX statement.
 */
export function parseSbiSavings(data: ArrayBuffer): ParsedStatement {
  const workbook = XLSX.read(new Uint8Array(data), { type: "array" })
  const sheet = workbook.Sheets[workbook.SheetNames[0]]
  if (!sheet) throw new Error("No sheet found in workbook")

  const rows: unknown[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" })

  const metadata = extractMetadata(rows)

  const headerIdx = findHeaderRow(rows)
  if (headerIdx === -1) throw new Error("Could not find column headers in SBI statement")

  const headerRow = rows[headerIdx] as string[]
  const colMap = mapColumns(headerRow)

  const transactions: ParsedTransaction[] = []
  for (let i = headerIdx + 1; i < rows.length; i++) {
    const row = rows[i] as unknown[]
    if (!row || row.length === 0) continue

    const txn = parseRow(row, colMap)
    if (txn) transactions.push(txn)
  }

  return {
    bank: "sbi",
    accountType: "savings",
    accountNumber: metadata.accountLast4,
    periodStart: metadata.periodStart,
    periodEnd: metadata.periodEnd,
    transactions,
  }
}

// ── Metadata extraction ──────────────────────────────────────────────────

interface StatementMetadata {
  accountLast4: string | null
  periodStart: string | null
  periodEnd: string | null
}

function extractMetadata(rows: unknown[][]): StatementMetadata {
  let accountLast4: string | null = null
  let periodStart: string | null = null
  let periodEnd: string | null = null

  for (let i = 0; i < Math.min(20, rows.length); i++) {
    const rowText = (rows[i] ?? []).map((c) => String(c ?? "")).join(" ")

    // "Account Number  :  33471515974"
    const accountMatch = rowText.match(/Account Number\s*:\s*(\d+)/)
    if (accountMatch) {
      accountLast4 = accountMatch[1].slice(-4)
    }

    // "Statement From  :  01-04-2025  to  31-03-2026"
    const periodMatch = rowText.match(
      /Statement From\s*:\s*(\d{2}-\d{2}-\d{4})\s+to\s+(\d{2}-\d{2}-\d{4})/i,
    )
    if (periodMatch) {
      periodStart = parseSbiDate(periodMatch[1])
      periodEnd = parseSbiDate(periodMatch[2])
    }
  }

  return { accountLast4, periodStart, periodEnd }
}

// ── Column detection ─────────────────────────────────────────────────────

interface ColumnMap {
  date: number
  details: number
  refNo: number
  debit: number
  credit: number
  balance: number
}

function findHeaderRow(rows: unknown[][]): number {
  for (let i = 0; i < Math.min(30, rows.length); i++) {
    const row = (rows[i] ?? []).map((c) => String(c ?? "").trim())
    if (
      row.includes(COLUMNS.date) &&
      row.includes(COLUMNS.details) &&
      row.includes(COLUMNS.debit)
    ) {
      return i
    }
  }
  return -1
}

function mapColumns(headerRow: string[]): ColumnMap {
  const find = (name: string): number => {
    const idx = headerRow.findIndex((h) => h.trim() === name)
    if (idx === -1) throw new Error(`Column "${name}" not found in SBI statement`)
    return idx
  }

  return {
    date: find(COLUMNS.date),
    details: find(COLUMNS.details),
    refNo: find(COLUMNS.refNo),
    debit: find(COLUMNS.debit),
    credit: find(COLUMNS.credit),
    balance: find(COLUMNS.balance),
  }
}

// ── Row parsing ──────────────────────────────────────────────────────────

function parseRow(row: unknown[], colMap: ColumnMap): ParsedTransaction | null {
  const rawDate = String(row[colMap.date] ?? "").trim()
  const date = parseSbiDate(rawDate)
  if (!date) return null

  // Details can be multi-line; join and clean
  const rawDetails = String(row[colMap.details] ?? "")
  const details = rawDetails.replace(/\n/g, " ").replace(/\s+/g, " ").trim()
  if (!details) return null

  const debit = parseAmount(row[colMap.debit])
  const credit = parseAmount(row[colMap.credit])

  if (debit === 0 && credit === 0) return null

  const amount = debit > 0 ? debit : credit
  const type: "debit" | "credit" = debit > 0 ? "debit" : "credit"

  const refNo = String(row[colMap.refNo] ?? "").trim() || null
  const closingBalance = parseAmount(row[colMap.balance]) || null

  const parsed = parseSbiNarration(details)

  return {
    date,
    description: details,
    amount,
    type,
    referenceNumber: refNo,
    valueDate: null,
    closingBalance,
    paymentMethod: parsed.paymentMethod,
    extractedName: parsed.name,
    upiId: parsed.upiId,
    sourceCategory: null,
  }
}

// ── SBI Narration Parser ─────────────────────────────────────────────────

interface NarrationInfo {
  paymentMethod: PaymentMethod | null
  name: string | null
  upiId: string | null
}

/**
 * Parse SBI narration formats:
 *   "WDL TFR   UPI/DR/509617867713/JANGU PH/HDFC/vyapar.168/Sent ..."
 *   "DEP TFR   UPI/CR/547529098706/K AYUSHI/ICIC/ayushikopp/UPI ..."
 *   "NEFT-HDFC0000001-SOME NAME-REF123"
 *   "INT.COLL."
 */
export function parseSbiNarration(narration: string): NarrationInfo {
  const upper = narration.toUpperCase()

  // UPI transactions: UPI/DR/<ref>/<name>/<bank>/<upi_id>/<label>
  const upiMatch = narration.match(/UPI\/(?:DR|CR)\/(\d+)\/([^/]+)\/([A-Z]{2,6})\/([^/\s]+)/i)
  if (upiMatch) {
    const name = upiMatch[2].trim()
    const upiId = upiMatch[4].trim()
    return {
      paymentMethod: "upi",
      name: name || null,
      upiId: upiId || null,
    }
  }

  // NEFT
  if (upper.includes("NEFT")) {
    const neftMatch = narration.match(/NEFT[-/][\w]+[-/]([\w\s]+?)(?:[-/]|$)/i)
    return {
      paymentMethod: "neft",
      name: neftMatch ? neftMatch[1].trim() : null,
      upiId: null,
    }
  }

  // RTGS
  if (upper.includes("RTGS")) {
    return { paymentMethod: "rtgs", name: null, upiId: null }
  }

  // IMPS
  if (upper.includes("IMPS")) {
    return { paymentMethod: "imps", name: null, upiId: null }
  }

  // ATM withdrawal
  if (upper.includes("ATM") || upper.includes("WDL ATM")) {
    return { paymentMethod: "atm", name: null, upiId: null }
  }

  // POS
  if (/\bPOS\b/.test(upper) || upper.includes("PURCHASE")) {
    return { paymentMethod: "pos", name: null, upiId: null }
  }

  // Interest credit
  if (upper.includes("INT.COLL") || upper.includes("INTEREST")) {
    return { paymentMethod: "other", name: "Interest", upiId: null }
  }

  return { paymentMethod: null, name: null, upiId: null }
}

// ── Utilities ────────────────────────────────────────────────────────────

/**
 * Parse SBI date formats:
 *   "DD/MM/YYYY" → "YYYY-MM-DD"   (transaction rows)
 *   "DD-MM-YYYY" → "YYYY-MM-DD"   (metadata rows)
 */
function parseSbiDate(raw: string): string | null {
  if (!raw) return null

  const match = raw.trim().match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/)
  if (!match) return null

  return `${match[3]}-${match[2]}-${match[1]}`
}
